import { getMeControllerGetUserInfoQueryKey } from '@/api/generated/default/default';
import { useMembershipControllerConfirmSubscriptionV1 } from '@/api/generated/membership/membership';
import { ROUTE_PAYMENT_COLLECT_PAYMENT_METHOD, ROUTE_QUESTIONNAIRE_V2 } from '@/constants/routes';
import useAnalyticsCalls from '@/hooks/use-analytics';
import useVisitorSession from '@/hooks/useVisitorSession';
import authService from '@/services/auth.service';
import { getRoute } from '@/utils';
import { useStripe } from '@stripe/react-stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Controller hook for handling payment verification and subscription confirmation.
 * This controller:
 * 1. Retrieves payment intent status from Stripe
 * 2. Confirms subscription if payment succeeds
 * 3. Handles error cases and redirects appropriately
 * 4. Tracks analytics for successful subscriptions
 */
export default function useVerifyPaymentControllerV2() {
  const stripe = useStripe();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const analytics = useAnalyticsCalls();
  const queryClient = useQueryClient();
  const clientSecret = params.get('payment_intent_client_secret') || params.get('setup_intent_client_secret');
  const subscriptionId = params.get('subscriptionId');
  const isTrial = params.get('trial') === 'true';
  const confirmSubscriptionMutation = useMembershipControllerConfirmSubscriptionV1();
  const hasProcessedPayment = useRef(false);
  const { email } = useVisitorSession();

  // Determine if this is a setup intent (trial) or payment intent (regular subscription)
  const isSetupIntent = params.get('setup_intent_client_secret') !== null;

  useEffect(() => {
    /**
     * Handles error cases by redirecting to payment collection page with error message
     * @param msg - Error message to display to user
     */
    async function handleError(msg = 'Something went wrong. Please try again.') {
      const errorUrl = new URLSearchParams();
      errorUrl.set('error', msg);
      if (isTrial) {
        errorUrl.set('trial', 'true');
      }
      navigate(`${getRoute(ROUTE_PAYMENT_COLLECT_PAYMENT_METHOD)}?${errorUrl.toString()}`, { replace: true });
    }

    /**
     * Confirms the subscription with the backend and handles post-subscription flow
     * @param subscriptionId - The ID of the subscription to confirm
     */
    const confirmSubscription = async (subscriptionId: string) => {
      try {
        const response = await confirmSubscriptionMutation.mutateAsync({ data: { subscriptionId: subscriptionId } });
        authService.login(response);
        analytics.callIdentifyMembership(response.userId, email ?? '', 'member');
        // Invalidate the user info query to refresh user data
        queryClient.invalidateQueries({ queryKey: getMeControllerGetUserInfoQueryKey() });
        navigate(ROUTE_QUESTIONNAIRE_V2, { replace: true });
      } catch (e) {
        // TODO: Error handle user already subscribed
        console.error('Subscription confirmation failed:', e);
        handleError('Subscription confirmation failed. Please try again.');
      }
    };

    /**
     * Main payment verification logic:
     * 1. Validates required parameters
     * 2. Retrieves payment intent status from Stripe
     * 3. Handles success/failure cases
     */
    async function handlePaymentVerification() {
      if (!clientSecret || !subscriptionId) {
        console.error(
          `Missing parameter: payment_intent_client_secret ${clientSecret} or subscriptionId ${subscriptionId}`,
        );
        await handleError();
        return;
      }

      if (!stripe || hasProcessedPayment.current) return;

      hasProcessedPayment.current = true;

      // For trials (setup intent), we just need to confirm the setup succeeded
      // For regular subscriptions (payment intent), we need to verify payment
      if (isSetupIntent) {
        stripe
          .retrieveSetupIntent(clientSecret)
          .then(({ setupIntent }) => {
            switch (setupIntent?.status) {
              case 'succeeded':
                console.log('Setup intent succeeded - confirming trial subscription');
                // For trials, no payment yet, so don't track revenue
                confirmSubscription(subscriptionId);
                break;
              default:
                console.error('Setup intent status:', setupIntent?.status);
                handleError('Payment setup failed. Please try again.');
                break;
            }
          })
          .catch((error) => {
            console.error('Failed to retrieve setup intent:', error);
            handleError();
          });
      } else {
        stripe
          .retrievePaymentIntent(clientSecret)
          .then(({ paymentIntent }) => {
            switch (paymentIntent?.status) {
              case 'succeeded':
                console.log('Payment succeeded');
                // Track analytics for successful payment
                analytics.callRevenue(paymentIntent.amount / 100);
                confirmSubscription(subscriptionId);
                break;
              default:
                // Payment failed - this is a user payment error, don't log to console
                handleError('Payment failed. Please try again.');
                break;
            }
          })
          .catch(handleError);
      }
    }

    handlePaymentVerification();
  }, [stripe, clientSecret, subscriptionId, isTrial, isSetupIntent, navigate, analytics, confirmSubscriptionMutation, email, queryClient]);

  return {};
}
